import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = [
    "input", "itemsContainer",
    "logoInput", "logoPreview",
    "subtotal", "discount", "vat", "vatType", "total", "sumTotal",
    "shopName", "shopPhone", "shopEmail", "shopAddress", "attendantName",
    "customerName", "customerPhone", "customerEmail", "customerAddress",
    "paymentReferenceNo", "paymentAmount", "paymentPayingBy", "paymentNote",
    "previewPaymentReferenceNo", "previewPaymentAmount", "previewPaymentPayingBy", "previewPaymentNote",
    "paymentDetails", "dueDate",
    "previewPaymentDetails", "previewDueDate",
    "previewShopName", "previewShopPhone", "previewShopEmail", "previewShopAddress", "previewAttendantName",
    "previewCustomerName", "previewCustomerPhone", "previewCustomerEmail", "previewCustomerAddress",
    "previewItemsContainer", "preview", "previewPayments"
  ]
  static optionalTargets = ["logoInput"]

  connect() {
    this._recalc = this.recalculateTotals.bind(this);
    this.element.addEventListener("input", this._recalc);
    this.element.addEventListener("change", this._recalc);
    this.recalculateTotals();
  }

  update(event) {
    const input = event.target
    const name = input.dataset.freeInvoicesTarget
    const previewName = "preview" + name.charAt(0).toUpperCase() + name.slice(1)

    const previewElements = this.targets.findAll(previewName)

    previewElements.forEach(el => {
      el.textContent = input.value.trim() || "-"
    })
  }

  updatePayment(event) {
    const formData = new FormData(this.element)

    const payments = [{
      payment_reference_no: formData.getAll("free_invoice[payments][][payment_reference_no]")[0] || "-",
      amount: formData.getAll("free_invoice[payments][][amount]")[0] || "-",
      paying_by: formData.getAll("free_invoice[payments][][paying_by]")[0] || "-",
      payment_note: formData.getAll("free_invoice[payments][][payment_note]")[0] || "-"
    }]

    this.previewPaymentsTargets.forEach(target => {
      target.innerHTML = ""

      payments.forEach(payment => {
        const row = document.createElement("tr")
        row.innerHTML = `
          <td style="padding:4px;">${payment.payment_reference_no}</td>
          <td style="padding:4px;">${payment.amount}</td>
          <td style="padding:4px;">${payment.paying_by}</td>
          <td style="padding:4px; word-wrap: break-word; overflow-wrap: break-word;">${payment.payment_note}</td>
        `
        target.appendChild(row)
      })
    })
  }

  syncItemsPreview() {
    if (!this.hasPreviewItemsContainerTarget) return

    this.previewItemsContainerTargets.forEach(container => {
      container.innerHTML = ""

      this.itemsContainerTarget.querySelectorAll(".item-row").forEach(row => {
        const name = row.querySelector('input[name*="[item_name]"]').value || "N/A"
        const qty = row.querySelector('input[name*="[quantity]"]').value || "1"
        const unitCost = parseFloat(row.querySelector('input[name*="[unit_cost]"]').value) || 0
        const amount = parseFloat(row.querySelector('input[name*="[amount]"]').value) || 0

        const tr = document.createElement("tr")
        tr.innerHTML = `
          <td class="bold">${name}</td>
          <td>${qty}</td>
          <td class="text-center">Kes ${unitCost.toFixed(2)}</td>
          <td class="text-right">Kes ${amount.toFixed(2)}</td>
        `
        container.appendChild(tr)
      })
    })
  }

  submit() {
    const email = this.inputTarget.value.trim()
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!email || !emailRegex.test(email)) {
      alert("Please enter a valid email address.")
      return
    }

    window.location.href = `/free_invoices/dashboard?email=${encodeURIComponent(email)}`
  }

  disconnect() {
    this.element.removeEventListener("input", this._recalc);
    this.element.removeEventListener("change", this._recalc);
  }

  addItem() {
    const firstItem = this.itemsContainerTarget.querySelector(".item-row")
    const newItem = firstItem.cloneNode(true)

    newItem.querySelectorAll("input").forEach(input => {
      if (input.type === "number" && input.name.includes("[quantity]")) {
        input.value = 1
      } else if (input.type === "number") {
        input.value = 0
      } else {
        input.value = ""
      }
    })

    const deleteBtn = newItem.querySelector(".delete-item")
    if (deleteBtn) {
      deleteBtn.style.display = "inline-block"
    }

    this.itemsContainerTarget.appendChild(newItem)
    this.syncItemsPreview()
    this.recalculateTotals()
  }

  deleteItem(event) {
    const itemRow = event.target.closest(".item-row")
    if (!itemRow) return

    if (this.itemsContainerTarget.children.length > 1) {
      itemRow.remove()
    }

    this.updateTotal()
    this.syncItemsPreview()
    this.recalculateTotals()
  }

  calculateAmount(event) {
    const row = event.target.closest(".item-row")
    if (!row) return

    const quantity = parseFloat(row.querySelector('input[name*="[quantity]"]').value) || 1
    const unitCost = parseFloat(row.querySelector('input[name*="[unit_cost]"]').value) || 0
    const amountField = row.querySelector('input[name*="[amount]"]')

    const amount = quantity * unitCost
    if (amountField) {
      amountField.value = amount.toFixed(2)
    }

    this.updateTotal()
    this.syncItemsPreview()
  }

  updateTotal() {
    let total = 0
    this.itemsContainerTarget.querySelectorAll('input[name*="[amount]"]').forEach(input => {
      total += parseFloat(input.value) || 0
    })
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = total.toFixed(2)
    }
  }

  recalculateTotals() {
    const vatSelect = document.getElementById("free_invoice_vat_type");
    const vatType = vatSelect ? vatSelect.value : "ov"; 
    let subtotal = 0.0;

    const itemAmountInputs = this.element.querySelectorAll("input[name*='free_invoice[items]'][name*='amount']");
    itemAmountInputs.forEach(inp => {
      const val = parseFloat((inp.value || "0").replace(/,/g, ""));
      subtotal += isNaN(val) ? 0 : val;
    });

    let discount = 0.0;
    const discountInput = this.element.querySelector("input[name='free_invoice[summary][discount]']");
    if (discountInput) {
      discount = parseFloat((discountInput.value || "0").replace(/,/g, ""));
      if (isNaN(discount)) discount = 0;
    }

    let shipping = 0.0;
    const shippingInput = this.element.querySelector("input[name='free_invoice[summary][shipping_charge]']");
    if (shippingInput) {
      shipping = parseFloat((shippingInput.value || "0").replace(/,/g, ""));
      if (isNaN(shipping)) shipping = 0;
    }

    let vatAmount = 0.0;
    if (vatType === "inclusive") {
      vatAmount = (subtotal - discount) - ((subtotal - discount) / 1.16);
    } else if (vatType === "exclusive") {
      vatAmount = (subtotal - discount) * 0.16;
    }

    let sumTotal = (subtotal - discount) + shipping;
    if (vatType === "exclusive") {
      sumTotal += vatAmount;
    }

    if (this.hasSubtotalTarget) this.subtotalTarget.textContent = `Kes ${subtotal.toFixed(2)}`;
    if (this.hasVatTarget) this.vatTarget.textContent = `Kes ${vatAmount.toFixed(2)}`;
    if (this.hasDiscountTarget) this.discountTarget.textContent = `Kes ${discount.toFixed(2)}`;
    if (this.hasSumTotalTarget) this.sumTotalTarget.textContent = `Kes ${sumTotal.toFixed(2)}`;
    if (this.hasShippingTarget) this.shippingTarget.textContent = `Kes ${shipping.toFixed(2)}`;

    document.querySelectorAll("[data-invoice-preview-target='preview']").forEach(preview => {
      const subtotalEl = preview.querySelector("[data-preview-subtotal]");
      const sumTotalEl = preview.querySelector("[data-preview-sumtotal]");
      const vatEl = preview.querySelector("[data-preview-vat]");
      const discountEl = preview.querySelector("[data-preview-discount]");
      const shippingEl = preview.querySelector("[data-preview-shipping]");

      if (subtotalEl) subtotalEl.textContent = `Kes ${subtotal.toFixed(2)}`;
      if (sumTotalEl) sumTotalEl.textContent = `Kes ${sumTotal.toFixed(2)}`;
      if (vatEl) vatEl.textContent = `Kes ${vatAmount.toFixed(2)}`;
      if (discountEl) discountEl.textContent = `Kes ${discount.toFixed(2)}`;
      if (shippingEl) shippingEl.textContent = `Kes ${shipping.toFixed(2)}`;
    });

    this.syncItemsPreview();
  }

  previewLogo(event) {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = e => {
        this.logoPreviewTarget.src = e.target.result
        this.logoPreviewTarget.classList.remove("d-none")

        const hiddenInput = document.querySelector("#logo_base64")
        if (hiddenInput) hiddenInput.value = e.target.result
      }
      reader.readAsDataURL(file)
    }
  }

  async previewInvoice(event) {
    event.preventDefault()

    const button = event.currentTarget
    const form = button.closest("form")
    if (!form) return

    try {
      const formData = new FormData(form)
      formData.delete("free_invoice[logo]")
      formData.delete("logo_base64")
      const params = new URLSearchParams(formData).toString()

      const resp = await fetch(`/free_invoices/preview?${params}`, {
        method: "GET",
        headers: { "Accept": "text/html" }
      })

      if (!resp.ok) throw new Error("Preview request failed")
      this.recalculateTotals();

      const html = await resp.text()

      document.querySelectorAll("#previewModal").forEach(el => el.remove())
      document.body.insertAdjacentHTML("beforeend", html)

      const modalEl = document.getElementById("previewModal")
      if (!modalEl) {
        console.error("preview: modal element not found in response HTML")
        return
      }

      if (this.hasLogoInputTarget && this.logoInputTarget.files?.length > 0) {
        if (!this._logoObjectUrl) {
          this._logoObjectUrl = URL.createObjectURL(this.logoInputTarget.files[0])
        }

        modalEl.querySelectorAll("img[data-free-invoices-target='logoPreview']").forEach(img => {
          img.src = this._logoObjectUrl
          img.classList.remove("d-none")
        })

        modalEl.addEventListener("hidden.bs.modal", () => {
          this._revokeLogoObjectUrl()
        }, { once: true })
      }

      this._previewModalInstance = new Modal(modalEl)

      modalEl.addEventListener("hidden.bs.modal", () => {
        modalEl.remove()
        this._previewModalInstance = null
      }, { once: true })

      this._previewModalInstance.show()

    } catch (err) {
      console.error("Preview error:", err)
      alert("Unable to generate preview — check console")
    }
  }

  _revokeLogoObjectUrl() {
    if (this._logoObjectUrl) {
      URL.revokeObjectURL(this._logoObjectUrl)
      this._logoObjectUrl = null
    }
  }

  closePreview() {
    const modalEl = document.getElementById("previewModal")
    if (!modalEl) return

    modalEl.classList.remove("show")
    modalEl.style.display = "none"

    const backdrop = document.querySelector(".modal-backdrop")
    if (backdrop) backdrop.remove()

    document.body.classList.remove("modal-open")
    document.body.style.overflow = ""
  }

  async downloadPdf(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const form = button.closest("form");
    if (!form) return;

    const formData = new FormData(form);
    formData.delete("free_invoice[logo]")
    formData.delete("logo_base64")
    const reference = formData.get("free_invoice[reference]") || "invoice";

    try {
      const params = new URLSearchParams(formData).toString()

      const response = await fetch(`/free_invoices/download_pdf?${params}`, {
        method: "GET",
        headers: { "Accept": "application/pdf" }
      })

      if (!response.ok) throw new Error("PDF generation failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `${reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download error:", err);
      alert("Failed to generate PDF");
    }
  }
}
